export interface TickerObject {
  symbol: string;
  sentiment: Sentiment;
  price: number;
  contributorCount: number;
  submissionCount: number;
  symbolLabel?: string;
  name: string;
  targetPrice: number;
  targetDate: string;
}

export type Sentiment =
  | "really_bullish"
  | "bullish"
  | "neutral"
  | "bearish"
  | "really_bearish";

export type SortField =
  | "bullish"
  | "bearish"
  | "uniquePredictors"
  | "predictionsMade";

export enum ITickerIntervalsEnum {
  ONE_WEEK = "one_week",
  ONE_MONTH = "one_month",
  THREE_MONTHS = "three_months",
  SIX_MONTHS = "six_months",
  ONE_YEAR = "one_year",
}

export type ITickerDisplayData = {
  date: string;
} & Omit<Partial<Record<string, string>>, "date">;

export enum ChartTypeEnum {
  "line" = "line",
}

export enum ChartLineTypeEnum {
  "solid" = "solid",
  "dashed" = "dashed",
}

export enum LineType {
  TICKER = 'ticker',
  COLLECTIVE = 'collective',
  USER_SUBMISSION = 'userSubmission'
}

export interface TickerChartConfigItem {
  lineType: ChartLineTypeEnum;
  type: ChartTypeEnum;
  color: string;
  label: string;
  editable?: boolean;
}

export interface ITickerDetails {
  symbol: string;
  symbolLabel: string | null;
  disableSubmitButton?: boolean;
  disableEditButton?: boolean;
  currentCollectiveSentimentPrice?: string;
  currentTradingDay: string;
  name: string;
  sentiment: Sentiment;
  currentPrice: string;
  collectiveSentimentPrice: string;
  totalContributorCount: number;
  totalSubmissionCount: number;
  userSentimentPrice?: string;
  currentUserSentimentPrice?: string;
  todayCollectiveSentimentPrice: string;
  collectiveScore: number;
  displayData: ITickerDisplayData[];
  chartConfig: {
    dateFormat: string;
  } & Record<string, TickerChartConfigItem | string>;
}

export interface IStrikeZone {
  date: string;
  highest: number;
  lowest: number;
  reallyBullishPrice: number;
  bullishPrice: number;
  neutralPrice: number;
  bearishPrice: number;
  reallyBearishPrice: number;
}

export interface ITickerWindowLimits {
  strikeZone: IStrikeZone[];
  newPredictionLine: {
    fromDate: string;
    fromPrice: string;
    toDate: string;
    toPrice: string;
  };
}

export interface ITickerEditWindowLimits {
  strikeZone: IStrikeZone[];
}

export interface SlimTicker {
  symbol: string;
  name: string;
  symbolLabel: string;
  thumbnailUrl: string;
  currentPrice: number;
  sentimentData: SentimentData;
  timeFrameDetails: TimeFrameDetails;
  prevState?: PrevTickerState;
}

export type PrevTickerState = {
  tickerPrice?: number;
  collectiveSentimentPrice?: number;
  contributorCount: number;
}

export type SubmitSentimentResult = {
  collectiveSentimentPrice: number;
  contributorCount: number;
  currentPrice: number;
  maxPrice: number;
  minPrice: number;
  bullishIncrement: number;
  bearishIncrement: number;
  visitorsSentimentPrice: number;
  visitorSentimentLabel: SentimentLabel;
  collectiveSentimentLabel: SentimentLabel;
  prevState?: PrevTickerState;
}

export type SentimentData = {
  collectiveSentimentPrice?: number;
  visitorsSentimentPrice?: number;
  contributorCount: number;
  maxPrice: number;
  minPrice: number;
  visitorSentimentLabel?: SentimentLabel;
  collectiveSentimentLabel?: SentimentLabel;
  bullishIncrement: number; //Its this way because we need five points on each side
  bearishIncrement: number; //Its this way because we need five points on each side
}

export type TimeFrameDetails = {
  timeFrame: TimeFrame;
  timeFrameLabel?: string;
  chartValues: {
    lineColor: string;
    displayData: ITickerDisplayData[];
  }
}

export enum TimeFrame {
  ONE_DAY = "one_day",
  FIVE_DAYS = "five_days",
  ONE_MONTH = "one_month",
  SIX_MONTHS = "six_months",
  ONE_YEAR = "one_year"
}

export enum MarketType {
  STOCKS = "stocks",
  CRYPTO = "crypto"
}

export enum SentimentLabel {
  MAX_BULLISH = 'MAX BULLISH',
  VERY_BULLISH = 'VERY BULLISH',
  BULLISH = 'BULLISH',
  NO_CHANGE = 'NO CHANGE',
  BEARISH = 'BEARISH',
  VERY_BEARISH = 'VERY BEARISH',
  MAX_BEARISH = 'MAX BEARISH'
}